import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { Spinner } from './UI';
import { parseCFDI, IMPUESTO_LABEL } from '../cfdi';

// Vista previa embebida del PDF de la solicitud y sus comprobantes/XML
// adjuntos, para no tener que abrir cada archivo en una pestaña aparte al
// revisar (contador/programación). Reutiliza las mismas llamadas autenticadas
// que ya usan "Descargar PDF" y "Descargar" de cada adjunto — solo que en vez
// de disparar una descarga, el blob se muestra inline. Los XML se parsean con
// el mismo parser de CFDI que ya usa la captura de solicitudes, y se muestran
// como una ficha legible en vez de la marca cruda.
export function VisorAdjuntos({ endpoint, id, comprobantes = [], folio }) {
  const tabs = [
    { key: 'pdf', label: `PDF ${folio || ''}`.trim(), icon: '📄' },
    ...comprobantes.map(a => ({
      key: a._id,
      label: a.nombreOriginal,
      icon: a.tipo === 'xml' ? '📋' : a.tipo === 'pdf' ? '📄' : '🖼️',
      mimetype: a.mimetype,
      esXml: a.tipo === 'xml',
    })),
  ];
  const [activo, setActivo] = useState('pdf');
  const [urls, setUrls] = useState({});
  const [cfdiPorTab, setCfdiPorTab] = useState({}); // { [tabKey]: datos parseados | 'error' }
  const [verCrudo, setVerCrudo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const urlsRef = useRef(urls);
  urlsRef.current = urls;

  // Al cambiar de solicitud: libera los blobs de la anterior y arranca en la pestaña PDF
  useEffect(() => {
    setActivo('pdf');
    setUrls({});
    setCfdiPorTab({});
    setVerCrudo(false);
    return () => { Object.values(urlsRef.current).forEach(u => URL.revokeObjectURL(u)); };
  }, [id]);

  useEffect(() => {
    if (!id || urls[activo]) return;
    setLoading(true);
    setError(false);
    const tab = tabs.find(t => t.key === activo);
    const path = activo === 'pdf' ? `${endpoint}/${id}/pdf` : `${endpoint}/${id}/comprobantes/${activo}`;
    const fetcher = activo === 'pdf'
      ? api.downloadPdf(path)
      : api.downloadFile(path).then(r => { if (!r.ok) throw new Error('No disponible'); return r.blob(); });
    let cancelado = false;
    fetcher
      .then(async blob => {
        if (cancelado) return;
        setUrls(u => ({ ...u, [activo]: URL.createObjectURL(blob) }));
        if (tab?.esXml) {
          try {
            const texto = await blob.text();
            setCfdiPorTab(d => ({ ...d, [activo]: parseCFDI(texto, tab.label) }));
          } catch {
            setCfdiPorTab(d => ({ ...d, [activo]: 'error' }));
          }
        }
      })
      .catch(() => { if (!cancelado) setError(true); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, id, endpoint]);

  const tabActiva = tabs.find(t => t.key === activo);
  const esImagen = (tabActiva?.mimetype || '').startsWith('image/');
  const urlActiva = urls[activo];
  const cfdiActivo = cfdiPorTab[activo];
  const mostrarResumenCfdi = tabActiva?.esXml && cfdiActivo && cfdiActivo !== 'error' && !verCrudo;

  return (
    <div style={{ border: '1px solid var(--hair)', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 560, background: 'var(--panel)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflowX: 'auto', padding: '8px 8px 0', background: 'var(--hair-soft)', borderBottom: '1px solid var(--hair)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActivo(t.key)} title={t.label} style={{
            padding: '6px 10px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer',
            background: activo === t.key ? '#fff' : 'transparent',
            borderBottom: activo === t.key ? '2px solid #5e72e4' : '2px solid transparent',
            fontSize: 11.5, fontWeight: activo === t.key ? 700 : 500, color: activo === t.key ? 'var(--ink)' : 'var(--glass)',
            fontFamily: 'Inter, sans-serif',
            whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0,
          }}>{t.icon} {t.label}</button>
        ))}
        {tabActiva?.esXml && cfdiActivo && cfdiActivo !== 'error' && (
          <button onClick={() => setVerCrudo(v => !v)} style={{
            marginLeft: 'auto', flexShrink: 0, padding: '4px 9px', borderRadius: 6, border: '1px solid var(--hair)',
            background: verCrudo ? 'var(--plasma)' : 'var(--hair-soft)', color: verCrudo ? '#fff' : 'var(--glass)',
            fontSize: 10.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap',
          }}>{verCrudo ? '📋 Ver datos' : '🔍 Ver XML crudo'}</button>
        )}
      </div>
      <div style={{ flex: 1, position: 'relative', background: '#f5f3f3', overflowY: mostrarResumenCfdi ? 'auto' : 'hidden' }}>
        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner /></div>
        )}
        {error && !loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', color: 'var(--glass)', fontSize: 12.5 }}>
            No se pudo cargar la vista previa.
          </div>
        )}
        {!error && !loading && mostrarResumenCfdi && <CfdiResumen data={cfdiActivo} />}
        {!error && !loading && !mostrarResumenCfdi && urlActiva && (
          esImagen
            ? <img src={urlActiva} alt={tabActiva?.label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            : <iframe src={urlActiva} title={tabActiva?.label || 'preview'} style={{ width: '100%', height: '100%', border: 'none' }} />
        )}
      </div>
    </div>
  );
}

// Ficha legible con los datos del CFDI extraídos por el parser (frontend/src/cfdi.js),
// para que contador/programación validen sin tener que leer el XML crudo.
function CfdiResumen({ data }) {
  const campo = (label, value) => (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--hair-soft)' }}>
      <span style={{ fontSize: 10.5, color: 'var(--glass)', width: 130, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.03em', paddingTop: 1 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: 'var(--ink)', fontWeight: 500, wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );
  const fmt = (n) => (n || n === 0) ? Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 }) : '—';

  return (
    <div style={{ padding: 16, fontSize: 13 }}>
      {data.esEgreso && (
        <div style={{ marginBottom: 10, padding: '5px 10px', borderRadius: 6, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', color: 'var(--danger)', fontSize: 11.5, fontWeight: 700 }}>
          ➖ Nota de crédito (Egreso)
        </div>
      )}
      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Emisor</div>
      {campo('RFC', data.rfcEmisor)}
      {campo('Nombre', data.nombreEmisor)}

      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 6px' }}>Comprobante</div>
      {campo('Folio fiscal (UUID)', data.uuid)}
      {campo('Serie / Folio', [data.serie, data.folio].filter(Boolean).join(' / '))}
      {campo('Fecha', data.fecha)}
      {campo('Fecha timbrado', data.fechaTimbrado)}
      {campo('Uso CFDI', data.usoCfdi)}
      {campo('Método de pago', data.metodoPago)}
      {campo('Moneda', data.moneda)}

      <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 6px' }}>Importes</div>
      {campo('Subtotal', `$${fmt(data.subTotal)}`)}
      {data.descuento > 0 && campo('Descuento', `$${fmt(data.descuento)}`)}
      {(data.impuestos || []).map(cod => (
        <React.Fragment key={cod}>
          {campo(IMPUESTO_LABEL[cod] || cod, `$${fmt(data.impuestosImportes?.[cod])}`)}
        </React.Fragment>
      ))}
      {campo('Total', <b>{`$${fmt(data.total)}`}</b>)}

      {data.conceptos?.length > 0 && (
        <>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 6px' }}>
            Conceptos ({data.conceptos.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {data.conceptos.map((c, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 8px', background: 'var(--hair-soft)', borderRadius: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.descripcion || '—'}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', fontFamily: 'Geist Mono, monospace', flexShrink: 0 }}>
                  ${fmt(c.importe)}{c.iva ? ` (+IVA $${fmt(c.iva)})` : ''}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
